import React, { useEffect, useState } from 'react';
import {
  CheckCircle2, AlertTriangle, Loader2, ListChecks,
  Calendar, Users, Wrench, Clock, Building2, ExternalLink,
} from 'lucide-react';
import { previewCapacidad } from '../api';

export default function Paso7Verificacion({ payload, errores }) {
  const [capacidad, setCapacidad] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    previewCapacidad({
      fecha_inicio: payload.fecha_inicio,
      fecha_limite: payload.fecha_limite,
      horario_inicio: payload.horario_inicio,
      horario_fin: payload.horario_fin,
      duracion_cita: payload.duracion_cita,
      excluir_fines_de_semana: payload.excluir_fines_de_semana,
      excluir_festivos: payload.excluir_festivos,
      excluidos_manuales: payload.excluidos_manuales,
      tecnicos_ids: payload.tecnicos_ids,
      ambito: payload.ambito,
    })
      .then((res) => { if (!cancelado) setCapacidad(res); })
      .catch((e) => {
        if (!cancelado) {
          setError(
            e?.response?.data?.errores?.[0]?.mensaje ||
            e?.response?.data?.error ||
            'No se pudo calcular la capacidad.'
          );
        }
      })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line
  }, []);

  const usuariosCount = payload.usuarios_ids.length;
  const tecnicosCount = payload.tecnicos_ids.length;
  const subtareasCount = payload.subtareas.length;
  const esColaborador = payload.ambito === 'colaborador';

  const capacidadInsuficiente =
    capacidad &&
    capacidad.slots_totales < usuariosCount &&
    usuariosCount > 0;

  return (
    <div className="space-y-5 max-w-3xl">
      {errores.length > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-700 font-medium text-sm mb-1">
            <AlertTriangle className="w-4 h-4" />
            La campaña no puede activarse aún
          </div>
          <ul className="text-xs text-red-700 list-disc pl-5 space-y-0.5">
            {errores.map((e, i) => (
              <li key={i}>
                <strong>{e.campo}</strong>: {e.mensaje}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Card de ámbito */}
        <ResumenCard
          icon={esColaborador ? <ExternalLink className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
          label="Ámbito"
        >
          {esColaborador ? 'Colaboradores' : 'Internos'}
          <div className="text-xs text-slate-500 mt-1">
            {esColaborador
              ? 'Personas externas. Técnicos y usuarios provienen de la tabla de colaboradores.'
              : 'Empleados de la empresa.'}
          </div>
        </ResumenCard>

        <ResumenCard icon={<Calendar className="w-4 h-4" />} label="Periodo">
          {payload.fecha_inicio || '?'} — {payload.fecha_limite || '?'}
          <div className="text-xs text-slate-500 mt-1">
            {payload.horario_inicio}–{payload.horario_fin} · citas de{' '}
            {payload.duracion_cita} min
          </div>
        </ResumenCard>

        <ResumenCard icon={<Wrench className="w-4 h-4" />} label="Asignación">
          {payload.asignacion_modo === 'libre' ? 'Libre (el usuario elige)' : 'Automática'}
          <div className="text-xs text-slate-500 mt-1">
            Máx. cancelaciones: {payload.max_cancelaciones_default}
          </div>
        </ResumenCard>

        <ResumenCard icon={<Users className="w-4 h-4" />} label="Personas">
          <strong>{tecnicosCount}</strong> técnico{tecnicosCount !== 1 ? 's' : ''} ·{' '}
          <strong>{usuariosCount}</strong> usuario{usuariosCount !== 1 ? 's' : ''}
        </ResumenCard>

        <ResumenCard icon={<ListChecks className="w-4 h-4" />} label="Subtareas">
          {subtareasCount === 0
            ? 'Sin checklist'
            : `${subtareasCount} subtarea${subtareasCount !== 1 ? 's' : ''}`}
        </ResumenCard>
      </div>

      <div
        className={
          'border rounded-lg p-4 ' +
          (capacidadInsuficiente ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white')
        }
      >
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-medium text-slate-800">Capacidad estimada</h3>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Calculando…
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : capacidad ? (
          <div className="space-y-2">
            <div className="text-sm text-slate-700">
              <strong>{capacidad.dias_totales}</strong> día{capacidad.dias_totales !== 1 ? 's' : ''} ·{' '}
              <strong>{capacidad.slots_totales}</strong> slot{capacidad.slots_totales !== 1 ? 's' : ''} disponible{capacidad.slots_totales !== 1 ? 's' : ''}
              {usuariosCount > 0 && <> · {usuariosCount} usuarios a colocar</>}
            </div>

            {capacidad.slots_por_tecnico?.length > 0 && (
              <ul className="text-xs text-slate-500 space-y-0.5">
                {capacidad.slots_por_tecnico.map((t) => (
                  <li key={t.tecnico_id} className="flex justify-between max-w-md">
                    <span>{t.nombre || `Técnico #${t.tecnico_id}`}</span>
                    <span className="tabular-nums">{t.slots} slots</span>
                  </li>
                ))}
              </ul>
            )}

            {capacidad.avisos?.length > 0 && (
              <div className="text-xs text-amber-700 space-y-0.5 pt-2 border-t border-amber-100">
                {capacidad.avisos.map((a, i) => (
                  <div key={i} className="flex items-start gap-1">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            )}

            {capacidadInsuficiente && (
              <div className="flex items-start gap-2 pt-2 text-xs text-amber-700">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  La capacidad ({capacidad.slots_totales}) es menor que el número de
                  usuarios ({usuariosCount}). Considera ampliar el rango, añadir
                  técnicos o reducir la duración de las citas.
                </span>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {!loading && !error && capacidad && capacidad.slots_totales > 0 && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
          <CheckCircle2 className="w-4 h-4" />
          Todo listo. Pulsa <strong>Crear y activar</strong> para que los usuarios
          puedan empezar a reservar, o <strong>Guardar borrador</strong> para volver
          a editar más tarde.
        </div>
      )}
    </div>
  );
}

function ResumenCard({ icon, label, children }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
        {icon}
        {label}
      </div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}
